'use client';

import type { Message } from '@/utils/message-broker/types';
import { useEffect, useRef, useState } from 'react';
import { useAccountUm } from '@/hooks/use-account-um';
import { MessageBroker } from '@/utils/message-broker';
import { generateQuery } from '@/logic/query-string/generate-query-string';

type StaticPageIframeProps = {
    src?: string;
    query: Record<string, string | number | boolean>;
    loginRequired?: boolean;
    isLoggedIn: boolean;
};

const Iframe = ({ src = '', query, loginRequired, isLoggedIn }: StaticPageIframeProps) => {
    const { triggerLogin, getUser } = useAccountUm();
    const [height, setHeight] = useState('');
    const [iframeSrc, setIframeSrc] = useState(src);
    const scrollPositionRef = useRef<number | null>(null);

    // MessageBroker-based listener TODO investigate if we need all of these checks !!!
    const handleMessage = (message: Message<{ contentHeight: number; scrollTo?: number; scrollBack?: boolean }>) => {
        if (!src || !message) {
            return;
        }

        try {
            const url = new URL(src);
            if (url.origin === message.origin && message.data?.contentHeight) {
                setHeight(message.data.contentHeight.toString());
            }
        } catch (e) {
            console.error("[Iframe] Couldn't set content height properly: ", e);
        }

        try {
            // Scroll to a specific position
            if (message.data?.scrollTo !== undefined) {
                scrollPositionRef.current = window.scrollY; // save for restore
                window.scrollTo({ top: message.data?.scrollTo, behavior: 'smooth' });
                return;
            }

            // Restore the previous scroll position after modal closes
            if (message.data?.scrollBack && scrollPositionRef.current !== null) {
                window.scrollTo({ top: scrollPositionRef.current, behavior: 'smooth' });
                scrollPositionRef.current = null;
            }
        } catch (e) {
            console.error("[Iframe] Couldn't set scroll position properly: ", e);
        }
    };

    useEffect(() => {
        if (loginRequired && !isLoggedIn) {
            // TODO check if referrer path is needed, for now it is not used, also on casino-web
            // const referrerPath = window.location.pathname?.substring(1);
            triggerLogin();
        }

        (async () => {
            const user = await getUser();
            setIframeSrc(`${src}${await generateQuery(query, user?.username)}`);
        })();

        MessageBroker.subscribe(handleMessage);
        return () => {
            MessageBroker.unsubscribe(handleMessage);
        };
    }, []);

    if ((!isLoggedIn && loginRequired) || !src?.length) {
        return null;
    }

    return (
        <iframe
            className="iframe-component"
            style={{ height: height ? `${height}px` : 'auto' }}
            src={iframeSrc}
            allow="clipboard-write; web-share"></iframe>
    );
};

export default Iframe;
